import { Box, Button, Form, Spinner } from '@factorialco/deprecated-design-system'
import { Field, Form as FormObject } from 'factorial-form'
import { DocumentsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  DocumentForPreviewDocument,
  DocumentPreviewContentDocument,
  UpdateBasicInfoDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import InputText from 'modules/core/components/Inputs/Text'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import Esignature from 'modules/documents/components/DocumentPreview/Content/Esignature'
import RestoreFromTrashBinBox from 'modules/documents/components/DocumentPreview/Content/Esignature/RestoreFromTrashBinBox'
import FillablePDF from 'modules/documents/components/DocumentPreview/Content/FillablePDF'
import FormLayout from 'modules/documents/components/DocumentPreview/FormLayout'
import FolderSelector from 'modules/documents/components/Folders/FolderSelector'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { isInTrashBin, isPendingToFill, isSignable } from 'modules/documents/lib/Documents'
import { isWritableEmployeeFolder } from 'modules/documents/lib/Folders'
import { splitFileName } from 'modules/documents/lib/files/filenameUtils'
import DocumentForm from 'modules/documents/lib/forms/Document'
import PrimoboxStatusRow from 'modules/integrations_primobox/components/StatusRow'

interface Props {
  document: Document
  saving?: boolean
  disableFilleableConfirm?: boolean
  loading?: boolean
}

const Content = ({ document, saving, disableFilleableConfirm, loading }: Props) => {
  const [updateBasicInfo] = useMutation(UpdateBasicInfoDocument, {
    extractorFn: (data) => data.documentsMutations.updateBasicInfoDocumentsDocument,
  })
  const form = useUnsafeMemo(
    () =>
      new DocumentForm({
        filename: document.filename,
        folder_id: document.folder?.id,
      }),
    [document]
  )
  const space = document.space
  const employeeId = document.employee?.id

  const { data, loading: loadingDocumentPreview } = useQuery(DocumentPreviewContentDocument, {
    variables: {
      companyId: document.company.id,
      documentId: document.id,
      space,
      employeeIds: employeeId ? [employeeId] : [],
    },
  })

  const employee = data?.documents.documentsConnection.nodes[0]?.employee
  const customResourceFieldsData =
    compact(
      data?.documents.folders.find((folder) => folder.id === document.folder?.id)?.resourceFields ??
        []
    ) ?? []
  const customValuesData = data?.documents.documentsConnection.nodes[0]?.values ?? []
  const signatureRequests = data?.esignature.requestsConnection.nodes ?? []
  const folders = data?.documents.folders ?? []
  const features = data?.features.featuresConnection.nodes ?? []
  const primoboxDocumentStatus = data?.integrationsPrimobox.documentStatusesConnection?.nodes[0]

  const hasFeature = (featureName: string) =>
    features.find((feature) => feature.name === featureName)?.enabled

  const currentFolder = folders.find((folder) => folder.id === document.folder?.id)

  const documentName = document.filename
  const { fileName, fileExtension } = splitFileName(documentName)

  const extensionForm = useMemo(
    () =>
      new FormObject(
        {
          filename: fileName,
        },
        {
          filename: 'string',
        }
      ),
    [fileName]
  )

  const onChangeFilename = (field: Field) => {
    const value = field.value
    form.get('filename').set(`${value}${fileExtension}`)
  }

  const isCurrentUserASignee = !!document.signees?.find(
    (access_id) => access_id === employee?.access?.id
  )

  const canEditEmployeeDocumentsWithoutFolderForEverybody =
    data?.documents._policies?.withoutFolderEverybodyEditEmployeeDocumentsWithoutFolder?.can ??
    false
  const canEditEmployeeFolderForEverybody =
    data?.documents?.editableEmployeeFoldersConnection?.nodes.find(
      (f) => f.id === document.folder?.id
    )?._policies?.editEmployeeFolderForEverybody?.can ?? false
  const canEditEmployeeDocumentsForEverybody = document.folder?.id
    ? canEditEmployeeFolderForEverybody
    : canEditEmployeeDocumentsWithoutFolderForEverybody
  const canCompanyUseCompanyDocuments =
    data?.documents._policies?.companyUseCompanyDocuments.can ?? false

  const canUserSeeSignatures =
    canCompanyUseCompanyDocuments ||
    canEditEmployeeDocumentsForEverybody ||
    isCurrentUserASignee ||
    signatureRequests.length > 0

  const showEsignature = isSignable(document) && canUserSeeSignatures && !isPendingToFill(document)

  const canDelete = hasFeature('dev_document_delete_permission')
    ? document._policies?.delete?.can
    : (document._policies?.edit?.can ?? false)

  const showRestoreDocument = isInTrashBin(document) && canDelete

  const showDocumentFields = !isPendingToFill(document)

  const showCustomFields = document.folder?.id && !isPendingToFill(document)

  const showFillableStatus = isPendingToFill(document)

  const showPrimobox = hasFeature('integrations_primobox_active')

  const canEdit = (): boolean => {
    return (
      (document._policies?.edit.can ?? false) &&
      !document.folder?.showOnly &&
      !isInTrashBin(document)
    )
  }

  const writableFolders = employee
    ? folders.filter((folder) =>
        isWritableEmployeeFolder(
          folder,
          data?.documents.documentsConnection.nodes[0]?.employee?._policies?.seeDocuments?.can ??
            false
        )
      )
    : folders

  const onSubmit = async () => {
    const { success } = await asyncAction(() => {
      const folderId = form.get('folder_id').value
        ? parseInt(form.get('folder_id').value)
        : undefined

      form.cleanAll()
      return updateBasicInfo({
        variables: {
          id: document.id,
          filename: form.get('filename').value,
          folderId: folderId,
        },
        refetchQueries: [DocumentsDataTableDocument, DocumentForPreviewDocument],
      })
    })

    if (success) {
      const fileNameWithoutExtension = splitFileName(form.get('filename').value).fileName
      extensionForm.get('filename').set(fileNameWithoutExtension)
    }
  }

  const isLoading = loading || loadingDocumentPreview

  if (isLoading)
    return (
      <Box padding="s96" width="full" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Box paddingY="s32" paddingX="s24" gap="s48" width="full" flexGrow>
      {showRestoreDocument && (
        <RestoreFromTrashBinBox documentId={document.id} documentDeletedAt={document.deletedAt} />
      )}
      {showDocumentFields && (
        <FormLayout>
          <Form onSubmit={onSubmit} padding={null}>
            <InputText
              label={i18n.t('documents.modal.file_name')}
              field={extensionForm.get('filename')}
              readonly={!canEdit()}
              onChange={onChangeFilename}
            />
            <FolderSelector
              folders={writableFolders}
              {...(currentFolder ? { initialFolder: currentFolder } : {})}
              label={i18n.t('split_documents.form.select_label')}
              onChange={(value) => form.get('folder_id').set(value)}
              readOnly={!canEdit()}
            />
            {canEdit() && form.isDirty && (
              <Form.Buttons>
                <Button submit>{i18n.t('common.submit')}</Button>
              </Form.Buttons>
            )}
          </Form>
        </FormLayout>
      )}
      {showFillableStatus && (
        <FillablePDF
          saving={!!saving}
          disableFilleableConfirm={!!disableFilleableConfirm}
          document={document}
          signatureRequests={signatureRequests}
        />
      )}
      {showEsignature && <Esignature document={document} />}
      {showPrimobox && primoboxDocumentStatus && (
        <Box style={{ margin: '-32px -64px' }}>
          <PrimoboxStatusRow status={primoboxDocumentStatus} inModal />
        </Box>
      )}

      {showCustomFields && (
        <Box style={{ margin: '-32px -64px' }}>
          <CustomFieldsForm
            padFormDirection="vertical"
            boxed={false}
            title={i18n.t('documents.custom_fields.title')}
            description={i18n.t('documents.custom_fields.description')}
            customResourceFieldsData={customResourceFieldsData}
            customValuesData={customValuesData}
            valuableId={document.id}
            valuableType="Document"
            currency={employee?.legalEntity?.currency}
          />
        </Box>
      )}
    </Box>
  )
}

export default observer(Content)
